import { useEffect, useRef } from "react";

/**
 * Ambient, interactive node-network for the login brand panel ("Login Split v2").
 * A shape-free field of points connected to nearby neighbours; the cursor gently
 * pushes and lights up nearby nodes, which spring back. Pure Canvas 2D — no
 * dependencies. Honours prefers-reduced-motion by rendering a single still frame.
 *
 * Defaults are the tuned values chosen for the full-panel treatment
 * (density 240 · link 122 · reach 125 · repel 0.20 · drift 0.85).
 */

interface LoginNetworkProps {
  className?: string;
  /** Node count. */
  density?: number;
  /** Max distance (px) between two nodes for an edge to be drawn. */
  link?: number;
  /** Cursor influence radius (px). */
  reach?: number;
  /** Cursor push strength, 0–1. */
  repel?: number;
  /** Ambient drift amount, 0–1. */
  drift?: number;
}

interface NetNode {
  hx: number;
  hy: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  ph: number;
  amp: number;
  sq: boolean;
}

type RGB = [number, number, number];

// Palette — matches the RefineAI login panel (muted cream at rest, olive-bright
// near the cursor).
const REST: RGB = [166, 162, 144];
const HOT: RGB = [183, 196, 107];

// Physics — the character of the motion.
const SPRING = 0.02;
const DAMP = 0.86;
const FORCE = 1.9;

const lerp = (a: number, b: number, m: number) => a + (b - a) * m;
const mix = (a: RGB, b: RGB, m: number): RGB => [
  lerp(a[0], b[0], m),
  lerp(a[1], b[1], m),
  lerp(a[2], b[2], m),
];
const rgba = (c: RGB, a: number) =>
  `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;

const LoginNetwork = ({
  className,
  density = 240,
  link = 122,
  reach = 125,
  repel = 0.2,
  drift = 0.85,
}: LoginNetworkProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let W = 0;
    let H = 0;
    let t = 0;
    let raf = 0;
    let nodes: NetNode[] = [];
    const mouse = { x: -9999, y: -9999, active: false };
    const reduceMedia = window.matchMedia("(prefers-reduced-motion: reduce)");

    const build = () => {
      nodes = [];
      for (let i = 0; i < density; i++) {
        const x = Math.random() * W;
        const y = Math.random() * H;
        nodes.push({
          hx: x,
          hy: y,
          x,
          y,
          vx: 0,
          vy: 0,
          ph: Math.random() * Math.PI * 2,
          amp: 2 + Math.random() * 6,
          sq: Math.random() < 0.5,
        });
      }
    };

    const nodeHeat = (n: NetNode) => {
      if (!mouse.active) return 0;
      const dx = n.x - mouse.x;
      const dy = n.y - mouse.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      const r = reach * 1.05;
      return d < r ? 1 - d / r : 0;
    };

    const step = () => {
      const driftOn = drift > 0;
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        let tx = n.hx;
        let ty = n.hy;
        if (driftOn) {
          tx += Math.sin(t * 0.6 + n.ph) * n.amp * drift;
          ty += Math.cos(t * 0.5 + n.ph * 1.3) * n.amp * drift;
        }
        let ax = (tx - n.x) * SPRING;
        let ay = (ty - n.y) * SPRING;

        if (mouse.active) {
          const dx = n.x - mouse.x;
          const dy = n.y - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < reach * reach) {
            const d = Math.sqrt(d2) || 1;
            const f = (1 - d / reach) * repel * FORCE;
            ax += (dx / d) * f;
            ay += (dy / d) * f;
          }
        }
        n.vx = (n.vx + ax) * DAMP;
        n.vy = (n.vy + ay) * DAMP;
        n.x += n.vx;
        n.y += n.vy;
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const link2 = link * link;

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < link2) {
            const d = Math.sqrt(d2);
            const prox = 1 - d / link;
            const heat = Math.max(nodeHeat(a), nodeHeat(b));
            const col = mix(REST, HOT, heat);
            ctx.strokeStyle = rgba(col, prox * (0.1 + heat * 0.5));
            ctx.lineWidth = 0.6 + heat * 0.8;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const heat = nodeHeat(n);
        const col = mix(REST, HOT, heat);
        const s = 2 + heat * 2.4;
        const alpha = 0.5 + heat * 0.45;
        if (n.sq) {
          ctx.fillStyle = rgba(col, alpha);
          ctx.fillRect(n.x - s / 2, n.y - s / 2, s, s);
        } else {
          ctx.strokeStyle = rgba(col, alpha);
          ctx.lineWidth = 1;
          ctx.strokeRect(n.x - s / 2, n.y - s / 2, s, s);
        }
      }
    };

    const loop = () => {
      t += 0.016;
      step();
      draw();
      raf = requestAnimationFrame(loop);
    };

    const start = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      if (reduceMedia.matches) {
        // Reduced motion: a single still frame at rest, no loop, no interaction.
        mouse.active = false;
        for (const n of nodes) {
          n.x = n.hx;
          n.y = n.hy;
          n.vx = 0;
          n.vy = 0;
        }
        draw();
      } else {
        loop();
      }
    };

    const resize = () => {
      const rect = container.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      if (W <= 0 || H <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
      start();
    };

    const onMove = (e: PointerEvent) => {
      if (reduceMedia.matches) return;
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    };
    const onLeave = () => {
      mouse.active = false;
    };
    const onMotionChange = () => start();

    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerleave", onLeave);
    reduceMedia.addEventListener("change", onMotionChange);

    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerleave", onLeave);
      reduceMedia.removeEventListener("change", onMotionChange);
      ro.disconnect();
    };
  }, [density, link, reach, repel, drift]);

  return (
    <div ref={containerRef} className={className} aria-hidden="true">
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
};

export default LoginNetwork;
